/**
 * usePreferences Hook
 *
 * Manages operator preferences state via localStorage/sessionStorage.
 * Follows the same localStorage pattern as useTooltip.ts.
 *
 * Preferences are collected via a deferred card that appears after:
 * 1. First training completion, OR
 * 2. 3+ days since first login (approximated by first page load)
 *
 * "Later" snoozes for the current session (sessionStorage).
 * "Save Preferences" persists permanently (localStorage).
 *
 * Will be upgraded to database persistence in Phase 21.1.4.
 */

import { useState, useCallback, useEffect } from 'react';

// localStorage / sessionStorage keys
const PREFS_KEY = 'op_preferences';
const FIRST_LOGIN_KEY = 'op_firstLoginDate';
const SNOOZED_KEY = 'op_preferencesSnoozed';

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

export interface OperatorPreferences {
  experienceLevel: string; // 'new' | 'less_than_6_months' | '6_to_12_months' | 'over_1_year'
  goals: string[]; // SOP names or generic categories
  dailyTimeMinutes: number; // 5 | 10 | 15
}

interface UsePreferencesReturn {
  /** Whether the preferences card should be visible */
  shouldShowCard: boolean;
  /** Saved preferences, or null if not yet saved */
  preferences: OperatorPreferences | null;
  /** "Later" -- hide for this session only */
  snooze: () => void;
  /** Save preferences permanently to localStorage */
  savePreferences: (prefs: OperatorPreferences) => void;
}

function getSavedPreferences(): OperatorPreferences | null {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OperatorPreferences;
  } catch {
    return null;
  }
}

function isSnoozed(): boolean {
  try {
    return sessionStorage.getItem(SNOOZED_KEY) === 'true';
  } catch {
    return false;
  }
}

function getFirstLoginDate(): Date | null {
  try {
    const raw = localStorage.getItem(FIRST_LOGIN_KEY);
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function setFirstLoginDate(): void {
  try {
    if (!localStorage.getItem(FIRST_LOGIN_KEY)) {
      localStorage.setItem(FIRST_LOGIN_KEY, new Date().toISOString());
    }
  } catch {
    // localStorage unavailable -- acceptable degradation
  }
}

function computeShouldShow(hasCompletedTraining: boolean): boolean {
  // Already saved? Don't show.
  if (getSavedPreferences()) return false;

  // Snoozed this session? Don't show.
  if (isSnoozed()) return false;

  // Ensure first login date is recorded
  setFirstLoginDate();

  // Check trigger conditions
  if (hasCompletedTraining) return true;

  const firstLogin = getFirstLoginDate();
  if (firstLogin) {
    const elapsed = Date.now() - firstLogin.getTime();
    if (elapsed >= THREE_DAYS_MS) return true;
  }

  return false;
}

export function usePreferences(hasCompletedTraining: boolean): UsePreferencesReturn {
  const [preferences, setPreferences] = useState<OperatorPreferences | null>(
    () => getSavedPreferences(),
  );
  // Initialize conservatively (trainingItems is undefined on first render).
  // Re-evaluate once training data loads via the useEffect below.
  const [shouldShowCard, setShouldShowCard] = useState(
    () => computeShouldShow(false),
  );

  // Re-run when hasCompletedTraining resolves from undefined → boolean.
  // This handles the case where the operator completes training in the same
  // session and the flag flips from false to true after data loads.
  useEffect(() => {
    setShouldShowCard(computeShouldShow(hasCompletedTraining));
  }, [hasCompletedTraining]);

  const snooze = useCallback(() => {
    try {
      sessionStorage.setItem(SNOOZED_KEY, 'true');
    } catch {
      // sessionStorage unavailable -- card stays visible until navigation
    }
    setShouldShowCard(false);
  }, []);

  const savePreferences = useCallback((prefs: OperatorPreferences) => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // localStorage unavailable -- preferences won't persist across sessions
    }
    setPreferences(prefs);
    setShouldShowCard(false);
  }, []);

  return { shouldShowCard, preferences, snooze, savePreferences };
}
